import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { Hooks } from "@xt/xtbase";
import { isFiatMarketCode } from "@/utils/fiatMarket";
import FiatCurrencyIcon from "../FiatCurrencyIcon";
import shellStyles from "../convertBottomSheet.module.less";
import { useConvertBottomSheet } from "../useConvertBottomSheet";
import {
  computeConvertFromToRate,
  formatConvertKlineDisplayPrice,
  formatConvertFallbackRatePrice,
  formatInvertedKlineRate,
  parseKlinePairCodes,
  useConvertKlineClosePrice,
} from "../convertKlineShared";
import { resolveConvertKlineSymbol } from "../convertKlineSymbol";
import { formatConvertResultAmount } from "../convertResultShared";
import styles from "./index.module.less";

const { useTranslation } = Hooks;

const ICON_CLOSE = require("../assets/result-close-union.svg").default;
const ICON_TRANSFER = require("../assets/transfer-1c.svg").default;

function formatPreviewReceiveAmount(str, currency) {
  if (str == null || str === "") return "";
  if (isFiatMarketCode(currency)) return formatConvertResultAmount(str, currency);
  return formatConvertKlineDisplayPrice(str) || "";
}

function formatDisplayAmount(amountStr, currency) {
  if (amountStr == null || amountStr === "") return "0";
  const n = parseFloat(String(amountStr).replace(/,/g, ""));
  if (!Number.isFinite(n)) return "0";
  const decimals = isFiatMarketCode(currency) ? 2 : 8;
  const s = n.toFixed(decimals).replace(/\.?0+$/, "");
  return s || "0";
}

const STABLE_USD_CODES = new Set(["USDT", "USDC", "USD"]);

function formatUsdApproxAmount(value) {
  const n = parseFloat(String(value ?? "").replace(/,/g, ""));
  if (!Number.isFinite(n)) return "";
  return n.toFixed(2).replace(/\.?0+$/, "") || "0";
}

function buildApproxUsdLabel(receiveCurrency, targetAmount, receiveUsdtPrice, receiveUsdtSymbol) {
  const code = (receiveCurrency || "").toUpperCase();
  if (!targetAmount) return "";

  if (STABLE_USD_CODES.has(code)) {
    const formatted = formatUsdApproxAmount(targetAmount);
    return formatted ? `≈ ${formatted} USD` : "";
  }

  const amount = parseFloat(String(targetAmount).replace(/,/g, ""));
  if (!Number.isFinite(amount)) return "";

  const usdtRate = computeConvertFromToRate(
    receiveCurrency,
    "USDT",
    receiveUsdtSymbol,
    receiveUsdtPrice
  );
  if (usdtRate == null) return "";

  const formatted = formatUsdApproxAmount(amount * usdtRate);
  return formatted ? `≈ ${formatted} USD` : "";
}

export default function ConvertConfirmSheet({
  open,
  onClose,
  onConfirm,
  spendCurrency,
  spendAmount,
  receiveCurrency,
  previewData,
  loading = false,
  convertMode,
  klineSymbol = "",
  klineClosePrice = "",
  priceCurrency = "",
  priceAmount = "",
  currencyOptionMap,
  cfdSymbolList = [],
  submitting = false,
}) {
  const t = useTranslation();
  const [rateInverted, setRateInverted] = useState(false);

  const {
    visible,
    sheetRef,
    dragZoneRef,
    closeWithAnimation,
    handleSheetAnimationEnd,
    overlayClassName,
    backdropClassName,
    sheetClassName,
    backdropStyle,
    sheetStyle,
  } = useConvertBottomSheet({ open, onClose, styles: shellStyles });

  const receiveUsdtSymbol = useMemo(
    () => resolveConvertKlineSymbol(receiveCurrency, "USDT", "swap", cfdSymbolList),
    [receiveCurrency, cfdSymbolList]
  );
  const receiveUsdtPrice = useConvertKlineClosePrice(receiveUsdtSymbol, "1");

  const { base: baseCode, quote: quoteCode } = useMemo(
    () => parseKlinePairCodes(klineSymbol),
    [klineSymbol]
  );

  useEffect(() => {
    if (open) setRateInverted(false);
  }, [open, spendCurrency, receiveCurrency, klineSymbol]);

  const receiveAmount = useMemo(() => {
    if (loading) return "...";
    if (previewData?.targetAmount != null) {
      return formatPreviewReceiveAmount(previewData.targetAmount, receiveCurrency) || "—";
    }
    return "—";
  }, [loading, previewData, receiveCurrency]);

  const toApproxLabel = useMemo(() => {
    if (loading || !previewData?.targetAmount) return "";
    return buildApproxUsdLabel(
      receiveCurrency,
      previewData.targetAmount,
      receiveUsdtPrice,
      receiveUsdtSymbol
    );
  }, [loading, previewData, receiveCurrency, receiveUsdtPrice, receiveUsdtSymbol]);

  const feeDisplay = useMemo(() => {
    if (loading) return "...";
    if (!previewData) return "—";
    const feeCurrencyCode = previewData.feeCurrencyCode;
    if (!feeCurrencyCode) return "—";
    const fee = formatConvertResultAmount(previewData.totalFee, feeCurrencyCode);
    if (!fee) return "—";
    return `${fee} ${String(feeCurrencyCode).toUpperCase()}`;
  }, [loading, previewData]);

  const marketPriceDisplay = useMemo(() => {
    const currency = (priceCurrency || "").toUpperCase();
    const amount = String(priceAmount ?? "").trim();
    if (!amount || !currency) return "—";
    return `${amount} ${currency}`;
  }, [priceAmount, priceCurrency]);

  const rateLabel = useMemo(() => {
    if (loading) return "...";
    if (klineClosePrice && baseCode && quoteCode) {
      if (rateInverted) {
        const inverted = formatInvertedKlineRate(klineClosePrice);
        return inverted ? `1 ${quoteCode} ≈ ${inverted} ${baseCode}` : "—";
      }
      return `1 ${baseCode} ≈ ${formatConvertKlineDisplayPrice(klineClosePrice)} ${quoteCode}`;
    }
    const data = previewData;
    const rate = data?.exchangeRate;
    if (!rate) return "—";
    const direct = formatConvertFallbackRatePrice(rate, convertMode);
    if (!direct) return "—";
    if (rateInverted) {
      const inverted = formatInvertedKlineRate(direct);
      return inverted ? `1 ${receiveCurrency} ≈ ${inverted} ${spendCurrency}` : "—";
    }
    return `1 ${spendCurrency} ≈ ${direct} ${receiveCurrency}`;
  }, [
    loading,
    klineClosePrice,
    baseCode,
    quoteCode,
    rateInverted,
    previewData,
    convertMode,
    spendCurrency,
    receiveCurrency,
  ]);

  if (!visible || typeof document === "undefined") return null;

  const getCurrencyColor = (code) => currencyOptionMap?.get(code)?.color || "#26A69A";

  return createPortal(
    <div className={overlayClassName} onClick={closeWithAnimation} role="presentation">
      <div className={backdropClassName} style={backdropStyle} aria-hidden="true" />
      <div
        ref={sheetRef}
        className={`${sheetClassName} ${styles.sheetScroll}`}
        style={sheetStyle}
        onAnimationEnd={handleSheetAnimationEnd}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={t("convert.confirmOrder")}
      >
        <div className={shellStyles.dragZone}>
          <div ref={dragZoneRef} className={shellStyles.topHandle} aria-hidden="true" />
        </div>

        <div className={styles.header}>
          <h3 className={styles.title}>{t("convert.confirmOrder")}</h3>
          <button
            type="button"
            className={styles.close}
            onClick={closeWithAnimation}
            aria-label={t("convert.close")}
          >
            <img src={ICON_CLOSE} alt="" width={13} height={13} draggable={false} />
          </button>
        </div>

        <div className={styles.body}>
          <div className={styles.currencyBlock}>
            <span className={styles.currencyLabel}>{t("convert.from")}</span>
            <div className={styles.currencyRow}>
              <FiatCurrencyIcon
                code={spendCurrency}
                className={styles.currencyIcon}
                style={{ background: getCurrencyColor(spendCurrency) }}
              />
              <span className={styles.currencyCode}>{spendCurrency}</span>
              <span className={styles.currencyAmount}>{formatDisplayAmount(spendAmount, spendCurrency)}</span>
            </div>
          </div>

          <div className={styles.currencyBlock}>
            <span className={styles.currencyLabel}>{t("convert.to")}</span>
            <div className={styles.currencyRow}>
              <FiatCurrencyIcon
                code={receiveCurrency}
                className={styles.currencyIcon}
                style={{ background: getCurrencyColor(receiveCurrency) }}
              />
              <span className={styles.currencyCode}>{receiveCurrency}</span>
              <span className={styles.currencyAmount}>{receiveAmount}</span>
            </div>
            {toApproxLabel ? <span className={styles.currencyApprox}>{toApproxLabel}</span> : null}
          </div>

          <div className={styles.divider} />

          <div className={styles.list}>
            <div className={styles.listItem}>
              <span className={styles.listItemTitle}>{t("convert.type")}</span>
              <span className={styles.listItemValue}>{t("convert.marketPrice")}</span>
            </div>
            <div className={styles.listItem}>
              <span className={styles.listItemTitle}>{t("convert.marketPrice")}</span>
              <span className={styles.listItemValue}>{marketPriceDisplay}</span>
            </div>
            <div className={styles.listItem}>
              <span className={styles.listItemTitle}>{t("convert.fee")}</span>
              <span className={styles.listItemValue}>{feeDisplay}</span>
            </div>
            <div className={styles.listItem}>
              <span className={styles.listItemTitle}>{t("convert.rate")}</span>
              <div className={styles.rateValue}>
                <span className={styles.rateText}>{rateLabel}</span>
                <button
                  type="button"
                  className={styles.rateToggle}
                  onClick={() => setRateInverted((v) => !v)}
                  aria-label={t("convert.swapDirection")}
                  disabled={loading || rateLabel === "..." || rateLabel === "—"}
                >
                  <img
                    className={styles.rateToggleIcon}
                    src={ICON_TRANSFER}
                    alt=""
                    width={14}
                    height={14}
                    draggable={false}
                  />
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className={styles.footer}>
          <button
            type="button"
            className={styles.confirmBtn}
            onClick={onConfirm}
            disabled={loading || submitting}
          >
            {t("convert.confirm")}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
